import { ReactNode, useEffect, useState, VFC } from 'react'
import { useFormContext } from 'react-hook-form'
import {
  errorRed,
  mediumGray,
  safetyOrange,
  truewhite,
} from 'src/styles/colors'
import { fontWeightSemiBold } from 'src/styles/font'
import styled from 'styled-components'
import {
  formInputStyle,
  FormItemProps,
  FormItemWrapper,
  invalidStyle,
} from './common'

const CONTENT_MAX_LENGTH = 800

type FormInputProps = FormItemProps & {
  placeholder?: string
  children?: ReactNode
  type?: string
  autoComplete?: string
  invalid?: boolean
  withoutBorder?: boolean
}

const ALL_STRING_REGEX = /.*/
const URL_REGEX = /https?:\/\//
const PHONE_REGEX = /^[0-9\-]{3,30}/
const EMAIL_REGEX =
  /^[A-Za-z0-9]{1}[A-Za-z0-9+_.-]*@{1}[A-Za-z0-9_.-]{1,}\.[A-Za-z0-9]{1,}$/

const valueRegex = (type?: string) => {
  switch (type) {
    case 'link':
      return URL_REGEX
    case 'phone':
      return PHONE_REGEX
    case 'mail':
      return EMAIL_REGEX
    default:
      return ALL_STRING_REGEX
  }
}

const invalidMessage = (type?: string) => {
  switch (type) {
    case 'link':
      return '不正なURLです。'
    case 'phone':
      return '不正な電話番号です。'
    case 'mail':
      return '不正なメールアドレスです。'
    default:
      return '不正な値です。'
  }
}

export const FormInput: VFC<FormInputProps> = ({
  placeholder,
  type,
  autoComplete,
  children,
  ...props
}) => {
  const { id, label, optional, invalid, withoutBorder } = props
  const { register, formState } = useFormContext()
  return (
    <FormItemWrapper {...props}>
      <Input
        id={id}
        placeholder={placeholder}
        type={type}
        autoComplete={autoComplete}
        {...register(id, {
          required: optional ? false : `${label}は必須です。`,
          pattern: {
            value: valueRegex(type),
            message: invalidMessage(type),
          },
        })}
        invalid={formState.errors[`${id}`]?.message || invalid}
        withoutBorder={withoutBorder}
      />
      <ErrorMessage>{formState.errors[`${id}`]?.message}</ErrorMessage>
      {children}
    </FormItemWrapper>
  )
}

export const FormTextarea: VFC<FormInputProps> = ({
  placeholder,
  children,
  ...props
}) => {
  const { id, optional } = props
  const { register, watch, formState } = useFormContext()
  const value = watch(id)
  return (
    <FormItemWrapper {...props}>
      <Textarea
        id={id}
        placeholder={placeholder}
        {...register(id, {
          required: props.optional ? false : `${props.label}は必須です`,
          maxLength: {
            value: CONTENT_MAX_LENGTH,
            message: `${CONTENT_MAX_LENGTH}文字まで入力可能です。`,
          },
        })}
      />
      <OutsideDiv>
        <ErrorMessage>{formState.errors[`${id}`]?.message}</ErrorMessage>
        <TextCount
          isRed={
            value?.length > CONTENT_MAX_LENGTH || (!optional && !value?.length)
          }
        >
          {value?.length || 0} / {CONTENT_MAX_LENGTH}
        </TextCount>
      </OutsideDiv>
    </FormItemWrapper>
  )
}

type FormInputMultipleProps = FormInputProps & {
  description: string
  max: number
}
export const FormInputMultiple: VFC<FormInputMultipleProps> = ({
  description,
  max,
  ...props
}) => {
  const { formState } = useFormContext()
  const error =
    formState.errors[`${props.id}`] && formState.errors[`${props.id}`][0]

  return (
    <FormInput {...props} id={`${props.id}[0]`} invalid={error}>
      <ErrorMessage>{error?.message}</ErrorMessage>
      <FormInputAdditional description={description} max={max} {...props} />
    </FormInput>
  )
}
const FormInputAdditional: VFC<FormInputMultipleProps> = ({
  description,
  max,
  ...props
}) => {
  const { register, watch, formState } = useFormContext()
  const { placeholder } = props
  const [numOfInputs, setNumOfInput] = useState(1)
  const values = watch(props.id)
  useEffect(() => {
    if (!Array.isArray(values)) return
    const length = values.filter(Boolean).length
    setNumOfInput(Math.max(numOfInputs, length))
  }, [values, numOfInputs, props.id])
  return (
    <>
      {Array.from(new Array(numOfInputs - 1)).map((_, idx) => {
        const id = `${props.id}[${idx + 1}]`
        const invalid =
          formState.errors[`${props.id}`] &&
          formState.errors[`${props.id}`][`${idx + 1}`]
        return (
          <>
            <AdditionalInput
              key={id}
              id={id}
              placeholder={placeholder}
              {...register(id, {
                required: props.optional ? false : `${props.label}は必須です。`,
                pattern: {
                  value: valueRegex(props.type),
                  message: invalidMessage(props.type),
                },
              })}
              invalid={invalid}
            />
            <ErrorMessage>{invalid?.message}</ErrorMessage>
          </>
        )
      })}
      <Description>{description}</Description>
      <AddButton
        type="button"
        onClick={() => setNumOfInput(Math.min(max, numOfInputs + 1))}
        disabled={numOfInputs >= max}
      >
        ＋ 入力欄を追加する
      </AddButton>
    </>
  )
}

export const Input = styled.input<{
  invalid?: boolean
  withoutBorder?: boolean
}>`
  ${formInputStyle};
  font-size: 16px;
  width: 100%;
  ${({ invalid }) => invalid && invalidStyle};
  ${({ withoutBorder }) =>
    withoutBorder && `background: #F8FAFC; border: unset; box-shadow: unset;`};
`
const Textarea = styled.textarea`
  ${formInputStyle};
  width: 100%;
  height: 160px;
  white-space: pre-wrap;
  resize: none;
`
const TextCount = styled.p<{ isRed: boolean }>`
  margin-top: 4px;
  font-size: 12px;
  ${({ isRed }) => isRed && `color: ${errorRed}`};
`

const AdditionalInput = styled(Input)<{ invalid: boolean }>`
  margin-top: 2%;
`

const Description = styled.p`
  color: ${mediumGray};
  margin-top: 6px;
  margin-bottom: 20px;
  font-size: 12px;
`

const AddButton = styled.button`
  width: 232px;
  height: 40px;
  margin: 8px 0;
  border: 1px solid ${safetyOrange};
  border-radius: 4px;

  font-size: 13px;
  font-weight: ${fontWeightSemiBold};
  line-height: 26px;
  text-align: center;
  color: ${safetyOrange};
  background-color: ${truewhite};
`

const ErrorMessage = styled.p`
  margin-top: 2px;
  font-size: 13px;
  color: ${errorRed};
`

const OutsideDiv = styled.div`
  display: flex;
  justify-content: space-between;
`
